import { CUSTOMER_API_PREFIX } from '../contracts/customer-constants.js';
import { normalizeOtpCode, toAsciiDigits } from '../contracts/phone.js';
import type {
  CustomerSessionDto,
  CustomerSessionLoginDto,
} from '../contracts/customer-auth.js';
import type { UserDto } from '../contracts/dto.js';
import type { ResolvedCustomerClientConfig } from './config.js';
import { CustomerApiError } from './errors.js';
import { HttpTransport, type CsrfTokenSource } from './http.js';

/**
 * Customer authentication state.
 *
 * This is the *only* place in the customer client that knows how a customer
 * signs in. The request widget asks it one question — "is the customer
 * authenticated?" — and, when the answer is no, hands control to the shared
 * authentication UI, which drives the methods below. No component implements
 * phone normalisation, OTP retries or session probing for itself.
 *
 * Two modes, one state object:
 *
 *  - `same-site` (Tamoomeh): Core is on the same registrable domain, so the
 *    httpOnly customer cookie is sent directly and the OTP exchange happens
 *    in-page with `fetch`. Nothing leaves the page and the draft survives
 *    trivially.
 *  - `sso` (Porabzar): Core is on a different registrable domain and its cookie
 *    cannot be used. This object probes the *relying party's* same-origin
 *    session instead, and `beginLogin()` navigates the browser into the
 *    authorization-code handoff. The code verifier and state live in the
 *    relying party's server session, never here.
 */

export interface CustomerAuthState {
  /** The signed-in customer, or null. */
  currentUser: UserDto | null;
  isAuthenticated: boolean;
  /** True while a session probe or OTP call is in flight. */
  loading: boolean;
  /** True once the first session probe has completed. */
  ready: boolean;
  /** Last failure, if any. */
  error: CustomerApiError | null;
  /** The OTP challenge currently awaiting a code, when one was requested. */
  pendingChallenge: { challengeId: string; phone: string; expiresIn: number; devCode?: string } | null;
  /** Which sign-in mechanism this deployment uses. */
  mode: ResolvedCustomerClientConfig['authMode'];
  /** Google succeeded but a contact phone is still required (no SMS OTP). */
  needsPhoneVerification: boolean;
  /** Server-advertised sign-in methods for this deployment. */
  authMethods: { phoneOtp: boolean; google: boolean };
}

export interface RequestOtpOutcome {
  challengeId: string;
  phone: string;
  expiresIn: number;
  /** Present only when the server explicitly enables development echo. */
  devCode?: string;
}

export interface BeginLoginInput {
  /** Customer section to return to after the handoff. */
  service?: string;
  /** Same-origin path to resume on. Defaults to the current URL. */
  returnTo?: string;
}

type Listener = (state: CustomerAuthState) => void;

export class CustomerAuth implements CsrfTokenSource {
  private state: CustomerAuthState;
  private readonly listeners = new Set<Listener>();
  private csrf: string | null = null;
  private probeInFlight: Promise<CustomerSessionDto> | null = null;

  constructor(
    private readonly config: ResolvedCustomerClientConfig,
    private readonly transport: HttpTransport,
  ) {
    this.state = {
      currentUser: null,
      isAuthenticated: false,
      loading: false,
      ready: false,
      error: null,
      pendingChallenge: null,
      mode: config.authMode,
      needsPhoneVerification: false,
      authMethods: { phoneOtp: true, google: false },
    };
    this.transport.setCsrfSource(this);
  }

  get csrfToken(): string | null {
    return this.csrf;
  }

  get snapshot(): CustomerAuthState {
    return this.state;
  }

  get currentUser(): UserDto | null {
    return this.state.currentUser;
  }

  get isAuthenticated(): boolean {
    return this.state.isAuthenticated;
  }

  get loading(): boolean {
    return this.state.loading;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Probe the current session.
   *
   * Concurrent callers share one in-flight probe: a page with three widgets must
   * not fire three session requests, and must not race on the CSRF token.
   */
  async refresh(): Promise<CustomerSessionDto> {
    if (this.probeInFlight) return this.probeInFlight;
    this.patch({ loading: true, error: null });
    const run = this.probe();
    this.probeInFlight = run;
    try {
      return await run;
    } finally {
      this.probeInFlight = null;
    }
  }

  private async probe(): Promise<CustomerSessionDto> {
    try {
      const { data } = await this.transport.send<CustomerSessionDto>({
        path: this.config.authMode === 'sso' ? this.bridgeUrl('sessionUrl') : `${CUSTOMER_API_PREFIX}/session`,
        method: 'GET',
        target: this.config.authMode === 'sso' ? 'bridge' : 'authority',
      });
      this.csrf = typeof data.csrfToken === 'string' ? data.csrfToken : null;
      this.patch({
        currentUser: data.user ?? null,
        isAuthenticated: Boolean(data.authenticated && data.user),
        loading: false,
        ready: true,
        error: null,
        needsPhoneVerification: Boolean(data.needsPhoneVerification),
        authMethods: {
          phoneOtp: data.authMethods?.phoneOtp !== false,
          google: Boolean(data.authMethods?.google),
        },
      });
      this.emit('session.probed', { authenticated: this.state.isAuthenticated });
      return data;
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      // A failed probe is not a failed sign-in: the customer is simply treated
      // as signed out, and the reason is kept so the UI can explain a bridge
      // that is not deployed.
      this.csrf = null;
      this.patch({
        currentUser: null,
        isAuthenticated: false,
        loading: false,
        ready: true,
        error: normalized,
        needsPhoneVerification: false,
        authMethods: { phoneOtp: true, google: false },
      });
      this.emit('session.probed', { authenticated: false, error: normalized.code });
      return { authenticated: false, user: null, csrfToken: '', expiresAt: null, services: [] };
    }
  }

  /**
   * Step 1 of the in-page flow: ask Core for a verification code.
   *
   * The phone is folded to ASCII digits and trimmed before it is sent, so
   * `۰۹۱۲…` and `٠٩١٢…` reach Core as `0912…`. Core normalises again
   * (`phoneSchema`) and is the authority: it still accepts `0098…`, `+98…` and
   * `0912…`, and still rejects anything that is not an Iranian mobile number.
   */
  async requestOtp(phone: string): Promise<RequestOtpOutcome> {
    this.assertSameSite('requestOtp');
    const normalizedPhone = normalizeNumericInput(phone);
    this.patch({ loading: true, error: null });
    try {
      const { data } = await this.transport.send<{
        challengeId: string;
        phone: string;
        expiresIn: number;
        devCode?: string;
      }>({
        path: `${CUSTOMER_API_PREFIX}/session/otp`,
        method: 'POST',
        body: { phone: normalizedPhone },
      });
      this.patch({
        loading: false,
        pendingChallenge: {
          challengeId: data.challengeId,
          phone: data.phone,
          expiresIn: data.expiresIn,
          ...(data.devCode ? { devCode: data.devCode } : {}),
        },
      });
      this.emit('otp.requested', { phone: data.phone });
      return data;
    } catch (error) {
      throw this.fail(error, 'auth.failed');
    }
  }

  /** Step 2: verify the code, establishing the httpOnly customer session. */
  async verifyOtp(input: { phone: string; code: string; challengeId?: string }): Promise<UserDto> {
    this.assertSameSite('verifyOtp');
    const phone = normalizeNumericInput(input.phone);
    // OTP: fold numerals and strip spaces/separators from autofill/paste. The
    // phone path only folds digits; separators there are part of normalisation
    // by Core's phoneSchema.
    const code = normalizeOtpCode(input.code);
    this.patch({ loading: true, error: null });
    try {
      const { data } = await this.transport.send<CustomerSessionLoginDto>({
        path: `${CUSTOMER_API_PREFIX}/session/verify`,
        method: 'POST',
        body: {
          phone,
          code,
          ...(input.challengeId ? { challengeId: input.challengeId } : {}),
        },
      });
      this.csrf = data.csrfToken;
      this.patch({
        currentUser: data.user,
        isAuthenticated: true,
        loading: false,
        ready: true,
        error: null,
        pendingChallenge: null,
      });
      this.emit('auth.completed', { isNewUser: data.isNewUser });
      return data.user;
    } catch (error) {
      throw this.fail(error, 'auth.failed');
    }
  }

  /**
   * Submit a contact phone for a pending Google identity.
   *
   * No OTP is sent. The pending cookie on Core identifies the Google `sub`;
   * this method only posts the phone. A full session is established only after
   * the server confirms the number was stored and linked.
   */
  async submitGooglePhone(phone: string): Promise<UserDto> {
    this.assertSameSite('submitGooglePhone');
    const normalizedPhone = normalizeNumericInput(phone);
    this.patch({ loading: true, error: null });
    try {
      const { data } = await this.transport.send<CustomerSessionLoginDto>({
        path: `${CUSTOMER_API_PREFIX}/auth/google/phone`,
        method: 'POST',
        body: { phone: normalizedPhone },
      });
      this.csrf = data.csrfToken;
      this.patch({
        currentUser: data.user,
        isAuthenticated: true,
        loading: false,
        ready: true,
        error: null,
        pendingChallenge: null,
        needsPhoneVerification: false,
      });
      this.emit('auth.completed', { isNewUser: data.isNewUser, provider: 'google' });
      return data.user;
    } catch (error) {
      throw this.fail(error, 'auth.failed');
    }
  }

  /**
   * Absolute URL that starts Google OIDC on Core.
   *
   * Only meaningful when `authMethods.google` is true and `authMode` is
   * same-site. The browser navigates top-level so the OAuth cookies are first-party
   * to the API host; after the callback Core redirects back to `returnTo`.
   */
  beginGoogleLogin(input: BeginLoginInput = {}): string {
    this.assertSameSite('beginGoogleLogin');
    if (!this.state.authMethods.google) {
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        'ورود با گوگل در این استقرار فعال نیست.',
      );
    }
    const base = this.config.apiBaseUrl?.replace(/\/+$/, '');
    if (!base) {
      throw new CustomerApiError('BRIDGE_UNAVAILABLE', 'آدرس سرویس احراز هویت پیکربندی نشده است.');
    }
    const url = new URL(`${base}${CUSTOMER_API_PREFIX}/auth/google/start`);
    url.searchParams.set('return_to', input.returnTo ?? currentHref());
    this.emit('auth.required', { service: input.service, provider: 'google' });
    return url.toString();
  }

  /**
   * Navigate into the cross-domain handoff.
   *
   * Returns the URL rather than navigating, so a caller can open a popup or a
   * full-page redirect and so the flow is testable without a browser.
   */
  beginLogin(input: BeginLoginInput = {}): string {
    if (this.config.authMode !== 'sso') {
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        'ورود مستقیم در این سایت فعال است و نیازی به انتقال به دامنه دیگر ندارد.',
      );
    }
    const url = new URL(this.bridgeUrl('loginUrl'), currentHref());
    if (input.service) url.searchParams.set('service', input.service);
    url.searchParams.set('return_to', input.returnTo ?? currentHref());
    this.emit('auth.required', { service: input.service });
    return url.toString();
  }

  /**
   * Sign out locally.
   *
   * Local by default: this clears *this* site's session and leaves the central
   * Tamoomeh Core session and any other relying site untouched. Pass `global: true` to
   * also revoke every session for the customer, which is the "sign out
   * everywhere" action and is deliberately a separate, explicit choice.
   */
  async logout(options: { global?: boolean } = {}): Promise<void> {
    this.patch({ loading: true, error: null });
    try {
      if (this.config.authMode === 'sso') {
        await this.transport.send<null>({
          path: this.bridgeUrl('logoutUrl'),
          method: 'POST',
          target: 'bridge',
          body: { global: Boolean(options.global) },
        });
      } else {
        await this.transport.send<null>({
          path: `${CUSTOMER_API_PREFIX}/session/logout`,
          method: 'POST',
          body: { global: Boolean(options.global) },
        });
      }
    } catch (error) {
      // A failed logout must not strand the UI in a "signed in" state that the
      // server no longer agrees with; the local state is cleared regardless.
      this.emit('auth.failed', { op: 'logout', error: String(error) });
    } finally {
      this.csrf = null;
      this.patch({
        currentUser: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        pendingChallenge: null,
        needsPhoneVerification: false,
      });
      this.emit('logout', { global: Boolean(options.global) });
    }
  }

  /** Clear a pending challenge, e.g. when the customer edits the phone number. */
  clearChallenge(): void {
    this.patch({ pendingChallenge: null });
  }

  private bridgeUrl(key: 'sessionUrl' | 'loginUrl' | 'logoutUrl'): string {
    const url = this.config.bridge?.[key];
    if (!url) {
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        'ورود از این سایت پیکربندی نشده است.',
      );
    }
    return url;
  }

  private assertSameSite(operation: string): void {
    if (this.config.authMode !== 'same-site') {
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        `«${operation}» فقط روی دامنه اصلی سرویس در دسترس است؛ در این سایت ورود از طریق انتقال امن انجام می‌شود.`,
      );
    }
  }

  private fail(error: unknown, event: 'auth.failed'): CustomerApiError {
    const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
    this.patch({ loading: false, error: normalized });
    this.emit(event, { code: normalized.code });
    return normalized;
  }

  private patch(partial: Partial<CustomerAuthState>): void {
    this.state = { ...this.state, ...partial };
    for (const listener of this.listeners) listener(this.state);
  }

  private emit(name: 'session.probed' | 'otp.requested' | 'auth.required' | 'auth.completed' | 'auth.failed' | 'logout', detail?: Record<string, unknown>): void {
    this.config.onEvent?.({ name, ...(detail ? { detail } : {}) });
  }
}

/**
 * Fold Persian (`۰-۹`) and Arabic-Indic (`٠-٩`) digits to ASCII and trim the
 * surrounding whitespace.
 *
 * Applied to the phone number and the OTP code at the one place the client
 * hands them to the transport, so every entry point behaves the same. It is a
 * convenience for the customer, not a security boundary: Core validates and
 * normalises the same values again and remains the authority.
 */
export function normalizeNumericInput(value: string): string {
  return toAsciiDigits(value).trim();
}

function currentHref(): string {
  return typeof location === 'undefined' ? 'http://localhost/' : location.href;
}
